import React from 'react';
import { FlexDiv, TextDefault } from './ui';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

function emptyHeadingRow() {
    return {
        text: '',
        prefix: '',
        scoreFrom: 0,
        scoreTo: 0,
        description_key: '',
    };
}

/**
 * Hidden scoring block: copy in `answer` only (see content/questions/62.json).
 */
export function BlockHiddenBlockEditor({ selectedBlock, onUpdateBlock }) {
    if (selectedBlock?.type !== 'blockHidden') {
        return null;
    }

    const answer = selectedBlock.answer || {};
    const heading = Array.isArray(answer.heading) ? answer.heading : [];

    const patchAnswer = (patch) => {
        onUpdateBlock({ answer: { ...answer, ...patch } });
    };

    return (
        <FlexDiv column gap={2}>
            <TextDefault bold>Hidden block (answer)</TextDefault>
            <TextDefault lighter>
                No learner-visible widget; scoring uses presentation actions.
                Edit review copy below.
            </TextDefault>
            {heading.map((row, index) => (
                <Paper
                    key={index}
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 1,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'grey.50',
                    }}
                >
                    <Stack spacing={1}>
                        <FlexDiv alignCenter gap={8}>
                            <TextDefault bold>Row {index + 1}</TextDefault>
                            {heading.length > 1 ? (
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        patchAnswer({
                                            heading: heading.filter(
                                                (_, i) => i !== index
                                            ),
                                        })
                                    }
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            ) : null}
                        </FlexDiv>
                        <TextField
                            size="small"
                            label="Prefix"
                            value={row.prefix ?? ''}
                            onChange={(e) => {
                                const next = heading.map((h, i) =>
                                    i === index
                                        ? { ...h, prefix: e.target.value }
                                        : h
                                );
                                patchAnswer({ heading: next });
                            }}
                            fullWidth
                        />
                        <TextField
                            size="small"
                            label="Text"
                            value={row.text ?? ''}
                            onChange={(e) => {
                                const next = heading.map((h, i) =>
                                    i === index ? { ...h, text: e.target.value } : h
                                );
                                patchAnswer({ heading: next });
                            }}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                        <FlexDiv gap={8} wrap>
                            <TextField
                                size="small"
                                type="number"
                                label="Score from"
                                value={row.scoreFrom ?? ''}
                                onChange={(e) => {
                                    const next = heading.map((h, i) =>
                                        i === index
                                            ? {
                                                  ...h,
                                                  scoreFrom: Number(e.target.value),
                                              }
                                            : h
                                    );
                                    patchAnswer({ heading: next });
                                }}
                            />
                            <TextField
                                size="small"
                                type="number"
                                label="Score to"
                                value={row.scoreTo ?? ''}
                                onChange={(e) => {
                                    const next = heading.map((h, i) =>
                                        i === index
                                            ? {
                                                  ...h,
                                                  scoreTo: Number(e.target.value),
                                              }
                                            : h
                                    );
                                    patchAnswer({ heading: next });
                                }}
                            />
                            <TextField
                                size="small"
                                label="Description key"
                                value={row.description_key ?? ''}
                                onChange={(e) => {
                                    const next = heading.map((h, i) =>
                                        i === index
                                            ? {
                                                  ...h,
                                                  description_key: e.target.value,
                                              }
                                            : h
                                    );
                                    patchAnswer({ heading: next });
                                }}
                                sx={{ flex: 1, minWidth: 140 }}
                            />
                        </FlexDiv>
                    </Stack>
                </Paper>
            ))}
            <FlexDiv>
                <IconButton
                    size="small"
                    onClick={() =>
                        patchAnswer({
                            heading: [...heading, emptyHeadingRow()],
                        })
                    }
                >
                    <AddIcon />
                </IconButton>
                <TextDefault lighter>Add heading row</TextDefault>
            </FlexDiv>
            <TextField
                size="small"
                label="Review heading text"
                value={answer.reviewHeadingText || ''}
                onChange={(e) =>
                    patchAnswer({ reviewHeadingText: e.target.value })
                }
                fullWidth
                multiline
                minRows={2}
            />
        </FlexDiv>
    );
}
